import { useCallback, useEffect, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/components/auth/AuthContext";
import type { OperatingSystemProxyTicket } from "@/lib/operating-systems";
import {
  beginScopedTicketRefresh,
  failScopedTicketRefresh,
  OperatingSystemTicketRequestRegistry,
  proxyTicketNeedsReplacement,
  proxyTicketRenewalUrl,
  readStoredOperatingSystemProxyTicket,
  removeStoredOperatingSystemProxyTicket,
  renewScopedTicketIfCurrent,
  scopedTicketForOperatingSystem,
  storeOperatingSystemProxyTicket,
  type ScopedTicketState,
} from "./operatingSystemProxyTicketState";

type TicketState = {
  ticket: OperatingSystemProxyTicket | null;
  loading: boolean;
  error: string | null;
  refreshTicket: () => Promise<OperatingSystemProxyTicket | null>;
  clearTicket: () => void;
};

const TICKET_REFRESH_SKEW_MS = 30_000;
type TicketRenewalOutcome = "renewed" | "retry" | "replaced";

function ticketStorage(): Storage | null {
  if (typeof window === "undefined") return null;
  try {
    return window.sessionStorage;
  } catch {
    return null;
  }
}

async function getInvokeErrorMessage(error: unknown): Promise<string> {
  const context = error && typeof error === "object" ? (error as { context?: unknown }).context : null;
  if (context instanceof Response) {
    const status = context.status;
    const payload = await context.clone().json().catch(() => null);
    if (payload && typeof payload === "object") {
      const record = payload as { message?: unknown; error?: unknown };
      if (typeof record.message === "string" && record.message.trim()) return record.message;
      if (typeof record.error === "string" && record.error.trim()) return record.error;
    }
    return `Runtime proxy request failed with HTTP ${status}.`;
  }

  return error instanceof Error ? error.message : "Unable to open the Applyt runtime proxy.";
}

export function useOperatingSystemProxyTicket(
  operatingSystemId: string | null | undefined,
  enabled: boolean,
  runtimeVersion: number | null | undefined,
): TicketState {
  const { runAuthenticatedRequest, authRecoveryState } = useAuth();
  const activeOperatingSystemId = enabled && operatingSystemId ? operatingSystemId : null;
  const activeRuntimeVersion = Number.isSafeInteger(runtimeVersion) && (runtimeVersion as number) >= 0
    ? runtimeVersion as number
    : null;
  const activeOperatingSystemIdRef = useRef<string | null>(activeOperatingSystemId);
  activeOperatingSystemIdRef.current = activeOperatingSystemId;
  const activeRuntimeVersionRef = useRef<number | null>(activeRuntimeVersion);
  activeRuntimeVersionRef.current = activeRuntimeVersion;
  const [scopedState, setScopedState] = useState<ScopedTicketState | null>(null);
  const scopedStateRef = useRef<ScopedTicketState | null>(scopedState);
  scopedStateRef.current = scopedState;
  const requestRegistryRef = useRef(new OperatingSystemTicketRequestRegistry());

  const stateForActiveOs = scopedTicketForOperatingSystem(scopedState, activeOperatingSystemId, activeRuntimeVersion);
  const ticket = stateForActiveOs?.ticket ?? null;
  const loading = stateForActiveOs?.loading ?? false;
  const error = stateForActiveOs?.error ?? null;

  const clearTicket = useCallback(() => {
    const activeId = activeOperatingSystemIdRef.current;
    const activeVersion = activeRuntimeVersionRef.current;
    if (activeId) removeStoredOperatingSystemProxyTicket(activeId, ticketStorage(), activeVersion);
    const currentState = scopedStateRef.current;
    const nextState = !activeId || (
      currentState?.operatingSystemId === activeId && currentState.runtimeVersion === activeVersion
    )
      ? null
      : currentState;
    scopedStateRef.current = nextState;
    setScopedState(nextState);
  }, []);

  const refreshTicket = useCallback(async () => {
    const requestOperatingSystemId = activeOperatingSystemIdRef.current;
    const requestRuntimeVersion = activeRuntimeVersionRef.current;
    if (!requestOperatingSystemId) {
      clearTicket();
      return null;
    }
    return requestRegistryRef.current.run(requestOperatingSystemId, requestRuntimeVersion, async () => {
      const loadingState = beginScopedTicketRefresh(
        scopedStateRef.current,
        requestOperatingSystemId,
        requestRuntimeVersion,
      );
      scopedStateRef.current = loadingState;
      setScopedState(loadingState);
      try {
        const data = await runAuthenticatedRequest(async () => {
          const { data, error: invokeError } = await supabase.functions.invoke("os-runtime-ticket", {
            body: { operatingSystemId: requestOperatingSystemId },
          });
          if (invokeError) throw invokeError;
          return data;
        });
        const proxyUrl = typeof data?.proxyUrl === "string" ? data.proxyUrl : "";
        const expiresAt = typeof data?.expiresAt === "string" ? data.expiresAt : "";
        if (!proxyUrl || !expiresAt) throw new Error("Runtime ticket response was incomplete.");
        const nextTicket = { proxyUrl, expiresAt };
        storeOperatingSystemProxyTicket(requestOperatingSystemId, nextTicket, ticketStorage(), requestRuntimeVersion);
        if (
          activeOperatingSystemIdRef.current === requestOperatingSystemId
          && activeRuntimeVersionRef.current === requestRuntimeVersion
        ) {
          const nextState = {
            operatingSystemId: requestOperatingSystemId,
            runtimeVersion: requestRuntimeVersion,
            ticket: nextTicket,
            loading: false,
            error: null,
          };
          scopedStateRef.current = nextState;
          setScopedState(nextState);
        }
        return nextTicket;
      } catch (err) {
        const message = await getInvokeErrorMessage(err);
        if (
          activeOperatingSystemIdRef.current === requestOperatingSystemId
          && activeRuntimeVersionRef.current === requestRuntimeVersion
        ) {
          const failedState = failScopedTicketRefresh(
            scopedStateRef.current,
            requestOperatingSystemId,
            requestRuntimeVersion,
            message,
          );
          scopedStateRef.current = failedState;
          setScopedState(failedState);
        }
        return null;
      }
    });
  }, [clearTicket, runAuthenticatedRequest]);

  const renewTicket = useCallback(async (): Promise<TicketRenewalOutcome> => {
    const requestOperatingSystemId = activeOperatingSystemIdRef.current;
    const requestRuntimeVersion = activeRuntimeVersionRef.current;
    const currentState = scopedTicketForOperatingSystem(
      scopedStateRef.current,
      requestOperatingSystemId,
      requestRuntimeVersion,
    );
    const currentTicket = currentState?.ticket;
    if (!requestOperatingSystemId || !currentTicket?.proxyUrl) return "replaced";

    let responseStatus: number | null = null;
    try {
      const response = await fetch(proxyTicketRenewalUrl(currentTicket.proxyUrl), {
        method: "POST",
        cache: "no-store",
      });
      responseStatus = response.status;
      const data = await response.json().catch(() => null) as { expiresAt?: unknown; error?: unknown } | null;
      const expiresAt = typeof data?.expiresAt === "string" ? data.expiresAt : "";
      if (!response.ok || !expiresAt) {
        throw new Error(
          typeof data?.error === "string"
            ? data.error
            : `Ticket renewal failed with HTTP ${response.status}.`,
        );
      }

      const renewedState = renewScopedTicketIfCurrent(
        scopedStateRef.current,
        requestOperatingSystemId,
        requestRuntimeVersion,
        currentTicket.proxyUrl,
        expiresAt,
      );
      if (
        activeOperatingSystemIdRef.current !== requestOperatingSystemId
        || !renewedState
      ) {
        return "replaced";
      }
      scopedStateRef.current = renewedState;
      setScopedState(renewedState);
      storeOperatingSystemProxyTicket(
        requestOperatingSystemId,
        { proxyUrl: currentTicket.proxyUrl, expiresAt },
        ticketStorage(),
        requestRuntimeVersion,
      );
      return "renewed";
    } catch (err) {
      const message = err instanceof Error ? err.message : "Unable to renew the runtime connection.";
      if (proxyTicketNeedsReplacement(responseStatus, currentTicket.expiresAt)) {
        const latestState = scopedTicketForOperatingSystem(
          scopedStateRef.current,
          requestOperatingSystemId,
          requestRuntimeVersion,
        );
        if (
          activeOperatingSystemIdRef.current === requestOperatingSystemId
          && activeRuntimeVersionRef.current === requestRuntimeVersion
          && latestState?.ticket?.proxyUrl === currentTicket.proxyUrl
        ) {
          removeStoredOperatingSystemProxyTicket(requestOperatingSystemId, ticketStorage(), requestRuntimeVersion);
          const clearedState = {
            operatingSystemId: requestOperatingSystemId,
            runtimeVersion: requestRuntimeVersion,
            ticket: null,
            loading: false,
            error: message,
          };
          scopedStateRef.current = clearedState;
          setScopedState(clearedState);
          await refreshTicket();
        }
        return "replaced";
      }

      const latestState = scopedTicketForOperatingSystem(
        scopedStateRef.current,
        requestOperatingSystemId,
        requestRuntimeVersion,
      );
      if (
        activeOperatingSystemIdRef.current === requestOperatingSystemId
        && activeRuntimeVersionRef.current === requestRuntimeVersion
        && latestState?.ticket?.proxyUrl === currentTicket.proxyUrl
      ) {
        const failedRenewalState = { ...latestState, error: message };
        scopedStateRef.current = failedRenewalState;
        setScopedState(failedRenewalState);
      }
      return "retry";
    }
  }, [refreshTicket]);

  useEffect(() => {
    if (!activeOperatingSystemId) {
      clearTicket();
      return;
    }
    if (!ticket && !loading) {
      const storedTicket = readStoredOperatingSystemProxyTicket(
        activeOperatingSystemId,
        ticketStorage(),
        Date.now(),
        TICKET_REFRESH_SKEW_MS,
        activeRuntimeVersion,
      );
      if (storedTicket) {
        const storedState = {
          operatingSystemId: activeOperatingSystemId,
          runtimeVersion: activeRuntimeVersion,
          ticket: storedTicket,
          loading: false,
          error: null,
        };
        scopedStateRef.current = storedState;
        setScopedState(storedState);
        return;
      }
      void refreshTicket();
    }
  }, [activeOperatingSystemId, activeRuntimeVersion, clearTicket, loading, refreshTicket, ticket]);

  useEffect(() => {
    if (!activeOperatingSystemId || !ticket?.expiresAt || authRecoveryState !== "ready") return undefined;
    const expiresAt = new Date(ticket.expiresAt).getTime();
    if (!Number.isFinite(expiresAt)) return undefined;
    const refreshInMs = Math.max(1_000, expiresAt - Date.now() - TICKET_REFRESH_SKEW_MS);
    let cancelled = false;
    let timer = window.setTimeout(async function renew() {
      const outcome = await renewTicket();
      if (outcome === "retry" && !cancelled) {
        timer = window.setTimeout(renew, 5_000);
      }
    }, refreshInMs);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [activeOperatingSystemId, authRecoveryState, renewTicket, ticket?.expiresAt]);

  return { ticket, loading, error, refreshTicket, clearTicket };
}
